import { z } from 'zod';
import type { AISchemaField, UISchemaField } from '../types/index';

export const productShowcaseAISchema: AISchemaField[] = [
  { name: 'eyebrow', type: 'string', required: false, max: 30 },
  { name: 'headline', type: 'string', required: true, max: 80 },
  { name: 'subheadline', type: 'string', required: false, max: 200 },
  {
    name: 'products',
    type: 'array<object>',
    required: true,
    fields: [
      { name: 'name', type: 'string', required: true, max: 30 },
      { name: 'category', type: 'string', required: false, max: 24, hint: 'カテゴリラベル (Workstation / Laptop など、hero_cards variant のみ表示)' },
      { name: 'tagline', type: 'string', required: true, max: 50 },
      { name: 'price', type: 'string', required: false, max: 24 },
      { name: 'image', type: 'image_url', required: false },
      {
        name: 'specs',
        type: 'array<object>',
        required: false,
        fields: [
          { name: 'label', type: 'string', required: true, max: 24 },
          { name: 'value', type: 'string', required: true, max: 60 },
        ],
      },
      { name: 'ctaLabel', type: 'string', required: false, max: 24 },
      { name: 'ctaHref', type: 'url', required: false },
    ],
  },
];

export const productShowcaseUISchema: UISchemaField[] = [
  { name: 'eyebrow', type: 'text', label: 'Eyebrow', max: 30 },
  { name: 'headline', type: 'text', label: 'Headline', required: true, max: 80 },
  { name: 'subheadline', type: 'textarea', label: 'Subheadline', max: 200 },
  { name: 'products', type: 'product_list', label: 'Products', required: true },
];

const specSchema = z.object({
  label: z.string().max(24),
  value: z.string().max(60),
});

export const productShowcaseZodSchema = z.object({
  eyebrow: z.string().max(30).optional().nullable(),
  headline: z.string().min(1).max(80),
  subheadline: z.string().max(200).optional().nullable(),
  products: z
    .array(
      z.object({
        name: z.string().max(30),
        category: z.string().max(24).optional().nullable(),
        tagline: z.string().max(50),
        price: z.string().max(24).optional().nullable(),
        image: z.string().optional().nullable(),
        specs: z.array(specSchema).optional().nullable(),
        ctaLabel: z.string().max(24).optional().nullable(),
        ctaHref: z.string().optional().nullable(),
      }),
    )
    .min(1)
    .max(4),
});

export type ProductShowcaseContent = z.infer<typeof productShowcaseZodSchema>;

export const productShowcaseSample: ProductShowcaseContent = {
  eyebrow: 'The lineup',
  headline: 'The lineup — three form factors.',
  subheadline: 'Each tuned for a different way of working.',
  products: [
    {
      name: 'Atlas Pro',
      tagline: 'For sustained, heavy workloads.',
      price: 'From $1,899',
      image: null,
      specs: [
        { label: 'CPU', value: 'M4 Max' },
        { label: 'Memory', value: 'up to 128GB' },
      ],
      ctaLabel: 'Configure',
      ctaHref: '#',
    },
    {
      name: 'Atlas Air',
      tagline: 'Studio power, anywhere.',
      price: 'From $1,099',
      image: null,
      specs: [
        { label: 'CPU', value: 'M4 Pro' },
        { label: 'Battery', value: '22 hr' },
      ],
      ctaLabel: 'Configure',
      ctaHref: '#',
    },
    {
      name: 'Atlas Mini',
      tagline: 'Compact desktop. Full Atlas.',
      price: 'From $599',
      image: null,
      specs: [
        { label: 'CPU', value: 'M4' },
        { label: 'Footprint', value: '127×127mm' },
      ],
      ctaLabel: 'Configure',
      ctaHref: '#',
    },
  ],
};
